import { MoneyText } from '@/components/MoneyText';
/**
 * Per-envelope Alerts dialog (TMMAPP-0021, design §11.6): the signed-in person's own email rules
 * on this envelope. Thresholds render in the response currency (the envelope owner's).
 * A NONE owner reads the list without write controls and sees the Subscribe card.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  notificationRulesApi,
  type AlertKind,
  type NotificationRule,
} from '@/api/notificationRules';
import { ErrorCodes, isApiError } from '@/api/errors';
import { ConfirmPanel } from '@/components/ConfirmDialog';
import { DialogDoneButton } from '@/components/DialogDoneButton';
import { ErrorState } from '@/components/ErrorState';
import { SubmitButton } from '@/components/SubmitButton';
import { useConfirm } from '@/components/useConfirm';
import { SubscribeCard } from '@/features/subscribe/SubscribeCard';
import { formatInstant, formatMoneyMinor, parseMoneyToMinor } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { useDialogFullScreen } from '@/shell/useDialogFullScreen';
import { useMe } from '@/session/meHooks';
import { GuardedDialog } from '@/components/GuardedDialog';
import { useDialogDirtyGuard } from '@/shared/useDialogDirtyGuard';
import { sameValues } from '@/shared/useDirty';

const ALL_KINDS: AlertKind[] = ['BALANCE_BELOW', 'BALANCE_ABOVE', 'LARGE_ALLOCATION'];

interface AlertsDialogProps {
  envelopeId: number;
  envelopeName: string;
  /** Envelope owner's operating currency — the Add field. Listed rows use `rule.currency`. */
  currency: string | null;
  writable: boolean;
  onClose: () => void;
  onNotify: (message: string) => void;
}

function parseThreshold(
  raw: string,
  kind: AlertKind,
  currency: string | null,
  locale: string,
): number | null {
  const minor = parseMoneyToMinor(raw, currency, locale);
  if (minor === null) return null;
  if (kind !== 'BALANCE_BELOW' && minor <= 0) return null;
  return minor;
}

export function AlertsDialog({
  envelopeId,
  envelopeName,
  currency,
  writable,
  onClose,
  onNotify,
}: AlertsDialogProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const me = useMe();
  const fullScreen = useDialogFullScreen();
  const zone = me.user.timezone ?? 'UTC';
  const [rules, setRules] = useState<NotificationRule[] | null>(null);
  const [loadFailure, setLoadFailure] = useState<{ error: unknown } | null>(null);
  const [lapsed, setLapsed] = useState(!writable);
  const [error, setError] = useState<string | null>(null);
  const [addKind, setAddKind] = useState<AlertKind>('BALANCE_BELOW');
  const [addAmount, setAddAmount] = useState('');
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editAmount, setEditAmount] = useState('');
  const deleting = useConfirm<NotificationRule>();
  const started = useRef(false);

  const canWrite = writable && !lapsed;
  // Typed-but-unsaved work: the new threshold, or an inline edit that differs from the saved one.
  const editingRule = rules?.find((rule) => rule.id === editingId);
  const dirty =
    !sameValues(addAmount, '') ||
    (editingRule !== undefined &&
      !sameValues(
        editAmount,
        formatMoneyMinor(editingRule.thresholdMinor, editingRule.currency, locale),
      ));
  const guard = useDialogDirtyGuard(dirty, onClose, adding);

  const load = useCallback(async () => {
    try {
      setRules(await notificationRulesApi.list(envelopeId));
      setLoadFailure(null);
    } catch (caught) {
      if (isApiError(caught) && caught.code === ErrorCodes.SUBSCRIPTION_REQUIRED) {
        setLapsed(true);
        setRules([]);
        setLoadFailure(null);
        return;
      }
      setLoadFailure({ error: caught });
    }
  }, [envelopeId]);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void load();
  }, [load]);

  const present = new Set((rules ?? []).map((rule) => rule.kind));
  const remaining = ALL_KINDS.filter((kind) => !present.has(kind));
  const kindValue = remaining.includes(addKind) ? addKind : (remaining[0] ?? 'BALANCE_BELOW');

  function writeError(caught: unknown) {
    if (isApiError(caught) && caught.code === ErrorCodes.SUBSCRIPTION_REQUIRED) {
      setLapsed(true);
      setError(null);
      return;
    }
    if (isApiError(caught) && caught.code === ErrorCodes.RULE_EXISTS) {
      void load();
      setError(t('alerts.exists'));
      return;
    }
    // VALIDATION_FAILED uses the generic line — 0020's applyBackendFields is not on this tree.
    setError(t('settings.errorGeneric'));
    guard.focusErrors();
  }

  async function handleAdd() {
    const minor = parseThreshold(addAmount, kindValue, currency, locale);
    if (minor === null || adding) return;
    setAdding(true);
    setError(null);
    try {
      await notificationRulesApi.create(envelopeId, {
        kind: kindValue,
        thresholdMinor: minor,
        enabled: true,
      });
      setAddAmount('');
      onNotify(t('alerts.saved'));
      await load();
    } catch (caught) {
      writeError(caught);
    } finally {
      setAdding(false);
    }
  }

  async function handleEnabled(rule: NotificationRule, enabled: boolean) {
    setRules((prev) =>
      prev === null ? prev : prev.map((row) => (row.id === rule.id ? { ...row, enabled } : row)),
    );
    setError(null);
    try {
      await notificationRulesApi.patch(envelopeId, rule.id, { enabled });
      await load();
    } catch (caught) {
      setRules((prev) =>
        prev === null
          ? prev
          : prev.map((row) => (row.id === rule.id ? { ...row, enabled: rule.enabled } : row)),
      );
      writeError(caught);
    }
  }

  async function handleSaveEdit(rule: NotificationRule) {
    const minor = parseThreshold(editAmount, rule.kind, rule.currency, locale);
    if (minor === null) {
      setError(t('alerts.positive'));
      guard.focusErrors();
      return;
    }
    setError(null);
    try {
      await notificationRulesApi.patch(envelopeId, rule.id, { thresholdMinor: minor });
      setEditingId(null);
      onNotify(t('alerts.saved'));
      await load();
    } catch (caught) {
      writeError(caught);
    }
  }

  async function handleDelete() {
    setError(null);
    await deleting.run(
      async (rule) => {
        await notificationRulesApi.remove(envelopeId, rule.id);
        onNotify(t('alerts.deleted'));
        await load();
      },
      (caught) => {
        if (isApiError(caught) && caught.code === ErrorCodes.SUBSCRIPTION_REQUIRED) {
          setLapsed(true);
          return null;
        }
        return t('settings.errorGeneric');
      },
    );
  }

  function lastFired(rule: NotificationRule): string {
    if (rule.lastFiredAt === null) return t('alerts.neverFired');
    try {
      return t('alerts.lastFired', { when: formatInstant(rule.lastFiredAt, locale, zone) });
    } catch {
      return t('alerts.neverFired');
    }
  }

  const addMinor = parseThreshold(addAmount, kindValue, currency, locale);

  // Deleting an alert confirms in place of the dialog's content — dialogs never stack (DLG-005).
  // Both returns are the same `GuardedDialog`, so the one `Dialog` stays mounted across the swap.
  if (deleting.target !== null) {
    return (
      <GuardedDialog
        guard={guard}
        confirm={{
          titleId: 'alerts-delete-title',
          consequenceId: 'alerts-delete-body',
          onClose: deleting.busy ? undefined : deleting.cancel,
        }}
        fullScreen={fullScreen}
        fullWidth
        maxWidth="sm"
      >
        <ConfirmPanel
          titleId="alerts-delete-title"
          consequenceId="alerts-delete-body"
          title={t('alerts.deleteTitle', {
            kind: t(`alerts.kind.${deleting.target.kind}`),
            amount: formatMoneyMinor(
              deleting.target.thresholdMinor,
              deleting.target.currency,
              locale,
            ),
          })}
          consequence={t('alerts.deleteConsequence')}
          confirmLabel={t('alerts.deleteAlert')}
          destructive
          busy={deleting.busy}
          error={deleting.error}
          focusHeading
          onConfirm={() => void handleDelete()}
          onCancel={deleting.cancel}
        />
      </GuardedDialog>
    );
  }

  return (
    <GuardedDialog
      guard={guard}
      titleId="alerts-title"
      fullScreen={fullScreen}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle id="alerts-title">{t('alerts.title', { envelope: envelopeName })}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {t('alerts.subtitle')}
          </Typography>
          {error !== null && <Alert severity="error">{error}</Alert>}
          {loadFailure !== null && (
            <ErrorState
              variant="section"
              error={loadFailure.error}
              what={t('errors.what.alerts')}
              onRetry={() => {
                setLoadFailure(null);
                void load();
              }}
            />
          )}
          {rules === null && loadFailure === null && <Skeleton variant="rounded" height={80} />}
          {rules !== null && rules.length === 0 && loadFailure === null && (
            <Typography variant="body2">{t('alerts.empty')}</Typography>
          )}
          {rules !== null &&
            rules.map((rule) => (
              <Box key={rule.id} sx={{ py: 1, borderBottom: 1, borderColor: 'divider' }}>
                <Stack direction="row" sx={{ alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Typography variant="body1">{t(`alerts.kind.${rule.kind}`)}</Typography>
                  {editingId === rule.id ? (
                    <TextField
                      label={t('alerts.threshold')}
                      value={editAmount}
                      onChange={(event) => setEditAmount(event.target.value)}
                      size="small"
                      helperText={rule.kind === 'BALANCE_BELOW' ? t('alerts.belowHelp') : undefined}
                    />
                  ) : (
                    <MoneyText minor={rule.thresholdMinor} currency={rule.currency} />
                  )}
                </Stack>
                <Stack
                  direction="row"
                  sx={{ alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 0.5 }}
                >
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {lastFired(rule)}
                  </Typography>
                  {canWrite && editingId === rule.id && (
                    <>
                      <Button size="small" onClick={() => void handleSaveEdit(rule)}>
                        {t('alerts.save')}
                      </Button>
                      <Button size="small" onClick={() => setEditingId(null)}>
                        {t('alerts.cancel')}
                      </Button>
                    </>
                  )}
                  {canWrite && editingId !== rule.id && (
                    <>
                      <Switch
                        checked={rule.enabled}
                        onChange={(event) => void handleEnabled(rule, event.target.checked)}
                        slotProps={{ input: { 'aria-label': t('alerts.enabled') } }}
                      />
                      <Button
                        size="small"
                        onClick={() => {
                          setEditingId(rule.id);
                          setEditAmount(
                            formatMoneyMinor(rule.thresholdMinor, rule.currency, locale),
                          );
                        }}
                      >
                        {t('alerts.edit')}
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        data-confirm-opener={`delete-alert-${rule.id}`}
                        onClick={() => deleting.ask(rule, `delete-alert-${rule.id}`)}
                      >
                        {t('alerts.delete')}
                      </Button>
                    </>
                  )}
                </Stack>
              </Box>
            ))}
          {canWrite && remaining.length > 0 && editingId === null && (
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              sx={{ gap: 1, alignItems: { sm: 'flex-start' } }}
            >
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel id="alerts-add-kind">{t('alerts.addKind')}</InputLabel>
                <Select
                  labelId="alerts-add-kind"
                  label={t('alerts.addKind')}
                  value={kindValue}
                  onChange={(event) => setAddKind(event.target.value as AlertKind)}
                >
                  {remaining.map((kind) => (
                    <MenuItem key={kind} value={kind}>
                      {t(`alerts.kind.${kind}`)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label={t('alerts.threshold')}
                value={addAmount}
                onChange={(event) => setAddAmount(event.target.value)}
                helperText={kindValue === 'BALANCE_BELOW' ? t('alerts.belowHelp') : undefined}
              />
              <SubmitButton
                onClick={() => void handleAdd()}
                disabled={addMinor === null || adding}
                loading={adding}
              >
                {t('alerts.add')}
              </SubmitButton>
            </Stack>
          )}
          {canWrite && remaining.length === 0 && rules !== null && (
            <Typography variant="body2">{t('alerts.allSet')}</Typography>
          )}
          {(!writable || lapsed) && <SubscribeCard />}
        </Stack>
      </DialogContent>
      <DialogActions>
        <DialogDoneButton onClick={guard.requestClose} />
      </DialogActions>
    </GuardedDialog>
  );
}
